import React from 'react';
import { SidebarLayout, Tooltip, Icon } from '@scuf/common';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { IRoute, SwitchRoutingTable } from '../../routes';
import { AuthenticatedUser } from '../../auth/models';
import './Sidebar.scss';
import i18next from '../../i18next';
import { Avatar, Popover, RecentIcon, useStatefulRef } from '@forge/common';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ReactComponent as SupportIcon } from '../../assets/help-support.svg';
import Support from '../../pages/Support/Support';
import { useAuthService } from '../../auth/hooks';

interface ISidebarProps {
  routes: IRoute[];
  user: AuthenticatedUser | null;
  collapsed: boolean;
  userProfileDetails: any; // Pass user profile details as a prop
  theme: number;
  onThemeChange: () => void;
  onModalClose: () => void;
  onOrganizationModalOpen: () => void;
  parentState?: any;
  newSideMenuBar: boolean;
  enableBacsFlow : boolean;
  activityLogVisible?: boolean; // Permission state from parent App.tsx
  organizationModalValue?: boolean;
}

export default function SidebarComponent({
  routes,
  user,
  collapsed,
  userProfileDetails,
  theme,
  onThemeChange,
  onModalClose,
  onOrganizationModalOpen,
  parentState,
  newSideMenuBar,
  enableBacsFlow,
  activityLogVisible = false,
  organizationModalValue = false
}: ISidebarProps) {
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const { enableSupportIconInSidebar } = useFlags();
  const { signIn, signOut } = useAuthService();
  const isRestrictedSidebarRole =
    parentState?.highestRole === 'ProjectManager' ||
    parentState?.highestRole === 'ProjectSupervisorExternal';

  const loadSidebarLayout = (featureRoute: any, index: any) => {
    return (
      <SidebarLayout.Sidebar.Item
        key={index}
        content={i18next.t(featureRoute.name)}
        icon={featureRoute.icon ? featureRoute.icon.icon : ''}
        iconRoot={featureRoute.icon ? featureRoute.icon.root : undefined}
        onClick={() => {
          history.push((routeMatch && routeMatch.path) + featureRoute.path);
        }}
        active={history.location.pathname.includes(featureRoute.path)}
        className={`sidebar-${featureRoute.name}`}
      />
    );
  };

  const anchor4 = useStatefulRef<HTMLButtonElement>();
  const supportRoute = {
    path: '/support',
    name: 'support',
    icon: {
      icon: <SupportIcon className="user-mgmt-new-icon help-support-icons" />,
      root: 'common'
    },
    component: <Support loggedInUser={user} />
  };
  const activityRoute = {
    path: '/feature/activity-log',
    name: 'activityLogSiTitle',
    icon: {
      icon: <RecentIcon
              className="user-mgmt-new-icon"
              style={{ width: '22px', height: '22px', margin: '0 10px 0 0' }}
            />,
      root: 'common'
    },
    component: null
  };
  const loginClick = async () => {
    await signIn();
  }

  const logoutClick = async () => {
    await signOut();
  }

  return (
    <SidebarLayout className="sidebar-height" collapsed={collapsed}>
      <SidebarLayout.Sidebar>
        {/* Render Routes */}
        <div className="sidebar-routes">
          {routes.map((featureRoute, index) => {
            return featureRoute.name && collapsed === true ? (
              <Tooltip
                key={index}
                element={loadSidebarLayout(featureRoute, index)}
                content={i18next.t(featureRoute.name)}
                className="tooltip-menu"
                offset="-200, 0"
              />
            ) : featureRoute.name ? (
              loadSidebarLayout(featureRoute, index)
            ) : null;
          })}
        </div>
        <div className='leftmenu-support-user'>
        <div className="sidebar-support-icon">
          { enableSupportIconInSidebar && !isRestrictedSidebarRole && (
            collapsed === true ? (
              <Tooltip
                element={loadSidebarLayout(supportRoute, 10)}
                content={i18next.t(supportRoute.name)}
                className="tooltip-menu"
                offset="-200, 0"
              />
            ) : loadSidebarLayout(supportRoute, 10)
          )
          }
        </div>
        {activityLogVisible && (<div className="sidebar-support-icon">
          {collapsed === true ? (
            <Tooltip
              element={loadSidebarLayout(activityRoute, 11)}
              content={i18next.t(activityRoute.name)}
              className="tooltip-menu"
              offset="-200, 0"
            />
          ) : loadSidebarLayout(activityRoute, 11)}
        </div>)}
        {/* User Profile Section */}
        {enableBacsFlow && (<div className="sidebar-profile-icon">
          <div className="grid grid-cols-2 justify-items-center gap-10">
            <Avatar interactive text={`${userProfileDetails?.firstName?.charAt(0).toUpperCase() || ''}${userProfileDetails?.lastName?.charAt(0).toUpperCase() || ''} `} ref={anchor4} />
          </div>
          <div className="col-start-5 row-start-2">
            <Popover id="popover-rt" position="right-top" anchor={anchor4} className="profile-popover">
              <div className="profile-popover-menu">
                
                {!parentState.user ? (
                  <span
                    className="profile-popover-menu-item"
                    onClick={loginClick}                                >
                    {i18next.t('login')}
                  </span>
                ) : (
                  <>
                    {enableBacsFlow &&(
                      <span
                        className="profile-popover-menu-item"
                        onClick={onOrganizationModalOpen}
                      >
                        {i18next.t('Organisation')}
                      </span>)}
                    <span
                      className="profile-popover-menu-item"
                      onClick={onModalClose}
                    >
                      {`${userProfileDetails?.firstName || ''} ${userProfileDetails?.lastName || ''}`}
                    </span>
                    <span
                      className="profile-popover-menu-item"
                      onClick={logoutClick}
                    >
                      {i18next.t('logout')}
                    </span>
                  </>
                )}

              </div>
            </Popover>
          </div>
        </div>)}
        </div>
       
      </SidebarLayout.Sidebar>
      {!organizationModalValue && (
        <SidebarLayout.Content className="sidebar-content-wrapper">
          <SwitchRoutingTable user={user} routes={routes} />
        </SidebarLayout.Content>
      )}
    </SidebarLayout>
  );
}